import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ToolResultProvider } from './contexts/ToolResultContext';
import { CreditGuardProvider } from './contexts/CreditGuardContext';
import ProtectedRoute from './components/ProtectedRoute';
import PortalLayout from './components/PortalLayout';
import ScrollToTop from './components/ScrollToTop';

// Landing Page Components
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import About from './components/About';
import Resources from './components/Resources';
import Contact from './components/Contact';
import Newsletter from './components/Newsletter';
import Footer from './components/Footer';
import LiveChat from './components/LiveChat';
import { translations } from './mock';

// Legal Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';

// Auth Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Verify2FA from './pages/Verify2FA';

// Portal Pages
import Portal from './pages/Portal';
import RoadmapCreator from './pages/RoadmapCreator';
import AgentBuilder from './pages/AgentBuilder';
import ProcessOptimizer from './pages/ProcessOptimizer';
import VirtualTraining from './pages/VirtualTraining';
import Analytics from './pages/Analytics';
import ToolHistory from './pages/ToolHistory';
import Profile from './pages/Profile';
import APIKeys from './pages/APIKeys';
import Billing from './pages/Billing';
import DesktopProbe from './pages/DesktopProbe';
import Reports from './pages/Reports';
import AIToolsDashboard from './pages/AIToolsDashboard';
import AICoach from './pages/AICoach';
import InsightExplorer from './pages/InsightExplorer';
import AutomationAssistant from './pages/AutomationAssistant';
import ScenarioSimulator from './pages/ScenarioSimulator';
import SalesAssistant from './pages/SalesAssistant';
import KnowledgeBrain from './pages/KnowledgeBrain';
import CustomerPulse from './pages/CustomerPulse';
import OpportunityRadar from './pages/OpportunityRadar';
import ProcessMapper from './pages/ProcessMapper';
import RiskDetector from './pages/RiskDetector';
import PricingOptimizer from './pages/PricingOptimizer';
import MeetingBrain from './pages/MeetingBrain';
import CashFlowPredictor from './pages/CashFlowPredictor';
import HiringAssistant from './pages/HiringAssistant';
import AgentAssistant from './pages/AgentAssistant';
import CompanyProfile from './pages/CompanyProfile';
import BusinessHealthCheck from './pages/BusinessHealthCheck';
import FinancialDashboard from './pages/FinancialDashboard';
import SmartAlerts from './pages/SmartAlerts';
import AccountingIntegrations from './pages/AccountingIntegrations';
import AdminConsole from './pages/AdminConsole';
import OrgAdmin from './pages/OrgAdmin';
import SSOCallback from './pages/SSOCallback';
import AgentWorkflows from './pages/AgentWorkflows';
import ImpersonateLanding from './pages/ImpersonateLanding';
import PaymentGate from './pages/PaymentGate';
import WritingAssistantPage from './pages/WritingAssistantPage';
import MeetingBrainLivePage from './pages/MeetingBrainLivePage';
import EmmaProactive from './pages/EmmaProactive';

function getDevcon9PreviewBasename() {
  const v2 = window.location.pathname.match(/^\/api\/preview\/[^/]+\/[^/]+/);
  if (v2) return v2[0];

  const legacy = window.location.pathname.match(/^\/api\/dev\/live\/[^/]+/);
  return legacy ? legacy[0] : "";
}


const LandingPage = () => {
  const { lang, toggleLang } = useLanguage();
  const t = translations[lang];

  const handleLanguageToggle = () => {
    toggleLang();
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToResources = () => {
    const element = document.getElementById('resources');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="landing-page">
      <Header t={t} lang={lang} onLanguageToggle={handleLanguageToggle} />
      <main>
        <Hero t={t} onCtaClick={scrollToContact} onSecondaryClick={scrollToResources} />
        <Services t={t} />
        <About t={t} />
        <Resources t={t} />
        <Contact t={t} />
        <Newsletter t={t} />
      </main>
      <Footer t={t} />
      <LiveChat t={t} />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
      <AuthProvider>
      <ToolResultProvider>
        <BrowserRouter basename={getDevcon9PreviewBasename()}>
        <CreditGuardProvider>
          <Routes>
            {/* Public Landing Page */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/sso-callback" element={<SSOCallback />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-2fa" element={<Verify2FA />} />
            <Route path="/payment-gate" element={<ProtectedRoute skipPaymentCheck><PaymentGate /></ProtectedRoute>} />
            <Route path="/impersonate" element={<ImpersonateLanding />} />
            
            {/* Protected Portal Routes — wrapped in PortalLayout */}
            <Route path="/portal" element={<ProtectedRoute><PortalLayout><Portal /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/org-admin" element={<ProtectedRoute><PortalLayout><OrgAdmin /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/dashboard" element={<ProtectedRoute><Navigate to="/portal" replace /></ProtectedRoute>} />
            <Route path="/portal/roadmap-creator" element={<ProtectedRoute><PortalLayout><RoadmapCreator /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/agent-builder-legacy" element={<ProtectedRoute><PortalLayout><AgentBuilder /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/process-optimizer" element={<ProtectedRoute><PortalLayout><ProcessOptimizer /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/virtual-training" element={<ProtectedRoute><PortalLayout><VirtualTraining /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/analytics" element={<ProtectedRoute><PortalLayout><Analytics /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/history" element={<ProtectedRoute><PortalLayout><ToolHistory /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/profile" element={<ProtectedRoute><PortalLayout><Profile /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/api-keys" element={<ProtectedRoute><PortalLayout><APIKeys /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/desktop-probe" element={<ProtectedRoute><PortalLayout><DesktopProbe /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/billing" element={<ProtectedRoute><PortalLayout><Billing /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/reports" element={<ProtectedRoute><PortalLayout><Reports /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools" element={<ProtectedRoute><PortalLayout><AIToolsDashboard /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/coach" element={<ProtectedRoute><PortalLayout><AICoach /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/explorer" element={<ProtectedRoute><PortalLayout><InsightExplorer /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/automation" element={<ProtectedRoute><PortalLayout><AutomationAssistant /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/simulator" element={<ProtectedRoute><PortalLayout><ScenarioSimulator /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/sales" element={<ProtectedRoute><PortalLayout><SalesAssistant /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/knowledge" element={<ProtectedRoute><PortalLayout><KnowledgeBrain /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/pulse" element={<ProtectedRoute><PortalLayout><CustomerPulse /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/radar" element={<ProtectedRoute><PortalLayout><OpportunityRadar /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/mapper" element={<ProtectedRoute><PortalLayout><ProcessMapper /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/risk" element={<ProtectedRoute><PortalLayout><RiskDetector /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/pricing" element={<ProtectedRoute><PortalLayout><PricingOptimizer /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/meeting" element={<ProtectedRoute><PortalLayout><MeetingBrain /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/cashflow" element={<ProtectedRoute><PortalLayout><CashFlowPredictor /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/hiring" element={<ProtectedRoute><PortalLayout><HiringAssistant /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/agent-builder" element={<ProtectedRoute><Navigate to="/portal/ai-tools" replace /></ProtectedRoute>} />
            <Route path="/portal/company-profile" element={<ProtectedRoute><PortalLayout><CompanyProfile /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/health-check" element={<ProtectedRoute><PortalLayout><BusinessHealthCheck /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/financial-dashboard" element={<ProtectedRoute><PortalLayout><FinancialDashboard /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/ai-tools/smart-alerts" element={<ProtectedRoute><PortalLayout><SmartAlerts /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/accounting" element={<ProtectedRoute><PortalLayout><AccountingIntegrations /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/agent-workflows" element={<ProtectedRoute><PortalLayout><AgentWorkflows /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/emma/writing" element={<ProtectedRoute><PortalLayout><WritingAssistantPage /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/emma/meeting-brain" element={<ProtectedRoute><PortalLayout><MeetingBrainLivePage /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            <Route path="/portal/emma-proactive" element={<ProtectedRoute><PortalLayout><EmmaProactive /><ScrollToTop /></PortalLayout></ProtectedRoute>} />
            
            {/* Admin Console (own auth) */}
            <Route path="/admin" element={<AdminConsole />} />
            
            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </CreditGuardProvider>
        </BrowserRouter>
      </ToolResultProvider>
      </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
